import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearReaderCalibration } from '../../app/calibration/calibrationSlice';
import {
  selectActiveFriendlyName,
  selectActiveMac,
} from '../../app/calibration/startCalibrationSlice';
import { rfidReaderMap } from '../../utils/tagCalibrationHelpers';

import { Grid, Dialog, Spinner } from '@enterprise-ui/canvas-ui-react';
import { Button } from 'ui-library';
import { RfidGraphic } from '../presentation/RfidGraphic';

import styles from '../../views/Calibration/TagWalkPage.module.scss';
import '../styles/spinnerStyles.scss';

export const ATRCalibrationContainer = ({
  activeState,
  directionState,
  completedChecks,
  lastReaderDetected,
  updateState,
  updateTime,
  stopTimer,
  resetCheck,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeFriendlyName = useSelector(selectActiveFriendlyName);
  const activeMac = useSelector(selectActiveMac);
  const friendlyLocation = activeFriendlyName?.split('-')[1];
  const [isQuitDialogVisible, setIsQuitDialogVisible] = useState(false);
  const [isLagDialogVisible, setIsLagDialogVisible] = useState(false);

  let referencePoint = rfidReaderMap?.get(directionState)?.referencePoint;
  let nextDirection = rfidReaderMap?.get(directionState)?.nextDirection;
  let armPosition = rfidReaderMap?.get(directionState)?.armPosition;
  let correctImg = rfidReaderMap?.get(directionState)?.correctImg;
  let wrongImg = rfidReaderMap?.get(directionState)?.wrongImg;
  let installerDirection =
    rfidReaderMap?.get(directionState)?.installerDirection;
  const commonButtons = (
    <>
      <Grid.Item justify="center" align="center">
        <Button
          label="Yes, I'm sure"
          onClick={() => {
            updateState('DONE', directionState);
          }}
        />
      </Grid.Item>
      <Grid.Item justify="center" align="center">
        <Button
          label="Oops, let me try again"
          onClick={() => {
            updateState('STARTED', directionState);
            resetCheck(true);
            // dispatch(clearReaderCalibration(activeMac));
          }}
        />
      </Grid.Item>
    </>
  );

  switch (activeState) {
    case 'STARTED':
      return (
        <>
          <Grid.Item
            data-testid="mainContent"
            xs={12}
            className="center-flex-block"
          >
            <p>To begin Calibration:</p>
            <ol>
              <li>
                Hold the test tag in your <strong>{armPosition}</strong> hand
              </li>
              <li>Extend your arm straight out to the side</li>
              <li>
                Press <strong>Ready</strong> to start the Calibration
              </li>
            </ol>
          </Grid.Item>
          <Grid.Item xs={12}>
            <RfidGraphic placement={directionState} variant={'portrait'} />
          </Grid.Item>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-sm"
          >
            <Grid.Item justify="center" align="center" xs={10}>
              <Button
                label="Ready"
                onClick={() => {
                  updateTime({ newStart: Date.now() });
                  updateState('IN_PROGRESS', directionState); // event handler should be dispatched based on this
                  // resetCheck(false);
                }}
                fullWidth
              />
            </Grid.Item>
            <Grid.Item justify="center" align="center" xs={10}>
              <Button
                label="Back"
                onClick={() => {
                  if (completedChecks % 2 > 0) {
                    //Allows user to go back and view confirmation pages on 1st and 3rd check, to review directions
                    setIsQuitDialogVisible(true);
                  } else {
                    updateState('CONFIRM_DISTANCE', directionState);
                  }
                }}
                type="secondary"
                fullWidth
              />
              <Dialog
                headingText="Are you sure?"
                bodyText="A calibration session is in progress. If you go back, the current session data will be lost."
                approveButtonText="Yes"
                isVisible={isQuitDialogVisible}
                onApprove={() => {
                  setIsQuitDialogVisible(false);
                  navigate('/calibrationSetupReader');
                  dispatch(clearReaderCalibration(activeMac));
                }}
                onRefuse={() => {
                  setIsQuitDialogVisible(false);
                }}
                refuseButtonText="Cancel"
              />
            </Grid.Item>
          </Grid.Container>
        </>
      );
    case 'CONFIRM_POSITION':
      return (
        <>
          <Grid.Item
            data-testid="mainContent"
            xs={12}
            className="center-flex-block"
          >
            <p className="hc-ta-center hc-fs-md" data-testid="started">
              To start:{' '}
            </p>
            <p className="hc-ta-center hc-fs-md">
              Stand facing the <strong>{referencePoint}</strong> and about{' '}
              <strong>3ft</strong> away from the reader.
            </p>
          </Grid.Item>
          <Grid.Item xs={12}>
            <RfidGraphic placement={correctImg} />
          </Grid.Item>
          <Grid.Item xs={12}>
            <RfidGraphic placement={wrongImg} />
          </Grid.Item>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-sm"
          >
            <Grid.Item justify="center" align="center" xs={10}>
              <Button
                label={`I am facing the ${referencePoint}`}
                onClick={() => {
                  updateState('CONFIRM_DISTANCE', directionState); // event handler should be dispatched based on this
                }}
                type="primary"
                fullWidth
              />
            </Grid.Item>
            <Grid.Item justify="center" align="center" xs={10}>
              <Button
                label="Back"
                onClick={() => {
                  if (completedChecks > 1) {
                    setIsQuitDialogVisible(true);
                  } else {
                    updateState('', directionState);
                  }
                }}
                type="secondary"
                fullWidth
              />
            </Grid.Item>
            <Dialog
              headingText="Are you sure?"
              bodyText="A calibration session is in progress. If you go back, the current session data will be lost."
              approveButtonText="Yes"
              isVisible={isQuitDialogVisible}
              onApprove={() => {
                setIsQuitDialogVisible(false);
                navigate('/calibrationSetupReader');
                dispatch(clearReaderCalibration(activeMac));
              }}
              onRefuse={() => {
                setIsQuitDialogVisible(false);
              }}
              refuseButtonText="Cancel"
            />
          </Grid.Container>
        </>
      );
    case 'CONFIRM_DISTANCE':
      return (
        <>
          <p className="hc-ta-center hc-fs-md" data-testid="started_2">
            Please confirm you are standing <strong>3ft</strong> from the
            reader.
          </p>
          <p className="hc-ta-center hc-fs-md" data-testid="started_2">
            <strong>Align yourself directly with the reader.</strong>
          </p>
          <Grid.Item xs={12}>
            <RfidGraphic placement={installerDirection} variant={'portrait'} />
          </Grid.Item>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-sm"
          >
            <Grid.Item justify="center" align="center" xs={10}>
              <Button
                label="I am 3ft from the reader"
                onClick={() => {
                  updateTime({ newStart: Date.now() });
                  updateState('STARTED', directionState); // event handler should be dispatched based on this
                }}
                fullWidth
              />
            </Grid.Item>
            <Grid.Item justify="center" align="center" xs={10}>
              <Button
                label="Back"
                onClick={() => {
                  updateState('CONFIRM_POSITION', directionState);
                }}
                type="secondary"
                fullWidth
              />
            </Grid.Item>
          </Grid.Container>
        </>
      );
    case 'SWAP_SIDES':
      return (
        <Grid.Container xs={12} justify="center" align="center">
          <Grid.Item xs={12}>
            <p className="hc-ta-center hc-fs-md">
              Now we'll calibrate the other side.
            </p>
          </Grid.Item>
          <Grid.Item xs={12}>
            <p className="hc-ta-center hc-fs-md">
              Please walk to face the other side of the reader.
            </p>
          </Grid.Item>
          <RfidGraphic placement={'ATR_SWAP_SIDES'} variant={'portrait'} />
          <Grid.Item justify="center" align="center" xs={12}>
            <Button
              label="Got it!"
              onClick={() => {
                updateState('CONFIRM_POSITION', nextDirection);
              }}
            />
          </Grid.Item>
        </Grid.Container>
      );
    case 'IN_PROGRESS':
      return (
        <Grid.Container xs={12} justify="center" align="center">
          <Grid.Item xs={12}>
            <p className="hc-ta-center hc-fs-md">
              Please stand still. Getting data...
            </p>
          </Grid.Item>
          <Grid.Item xs={12}>
            <p className="hc-ta-center hc-fs-sm spinnerWrapper">
              <span className="calibrationPosition">
                Calibrating <br />
                {friendlyLocation}
              </span>

              <Spinner className="spinnerSize" />
            </p>
          </Grid.Item>
          <Grid.Item xs={12}>
            <p className="hc-ta-center hc-fs-sm">
              This may take up to a minute.
            </p>
          </Grid.Item>
          <Grid.Item justify="center" align="center">
            <Button
              label="Back"
              onClick={() => {
                updateState('STARTED', directionState);
                stopTimer();
              }}
              type="secondary"
            />
          </Grid.Item>
          <Dialog
            headingText="Uh oh, something's wrong..."
            bodyText="The reader didn't quite get that. Please try again and make sure to stand as shown in the image."
            approveButtonText="Got it"
            isVisible={isLagDialogVisible}
            onApprove={() => {
              setIsLagDialogVisible(false);
            }}
          />
        </Grid.Container>
      );
    case 'UNKNOWN_LOCATION':
      return (
        <>
          <p className="hc-ta-center hc-fs-md">
            The test RFID card wasn’t clearly detected in the store.
          </p>
          <p className="hc-ta-center hc-fs-md">
            Are you sure you're standing near reader{' '}
            <strong>{activeFriendlyName}</strong>?
          </p>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-lg"
          >
            {commonButtons}
          </Grid.Container>
        </>
      );
    case 'WRONG_LOCATION':
      return (
        <>
          <p className="hc-ta-center hc-fs-md">
            Reader <em>{lastReaderDetected}</em> detected you instead of{' '}
            <em>{activeFriendlyName}</em>.
          </p>
          <p className="hc-ta-center hc-fs-md">
            Are you sure you're standing near reader{' '}
            <strong>{activeFriendlyName}</strong>?
          </p>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-lg"
          >
            {commonButtons}
          </Grid.Container>
        </>
      );
    case 'BACKWARDS_READER':
      return (
        <>
          <p className="hc-ta-center hc-fs-md">
            The RFID reader detected you on the other side.
          </p>
          <p className="hc-ta-center hc-fs-md">
            Are you sure you're standing as pictured?
          </p>
          <Grid.Item xs={12}>
            <RfidGraphic placement={directionState} />
          </Grid.Item>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-lg"
          >
            {commonButtons}
          </Grid.Container>
        </>
      );
    case 'UNKNOWN_ORIENTATION':
      return (
        <>
          <p className="hc-ta-center hc-fs-md">
            The RFID reader couldn't tell which side you were standing on.
          </p>
          <p className="hc-ta-center hc-fs-md">
            Are you sure you're standing as pictured?
          </p>
          <Grid.Item xs={12}>
            <RfidGraphic placement={directionState} />
          </Grid.Item>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-lg"
          >
            {commonButtons}
          </Grid.Container>
        </>
      );
    case 'WRONG_ANTENNA':
      return (
        <>
          <p className="hc-ta-center hc-fs-md">
            The RFID reader saw your tag in a different area than expected.
          </p>
          <p className="hc-ta-center hc-fs-md">
            Are you sure you're standing as pictured?
          </p>
          <Grid.Item xs={12}>
            <RfidGraphic placement={directionState} />
          </Grid.Item>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-lg"
          >
            {commonButtons}
          </Grid.Container>
        </>
      );
    case 'DONE':
      return (
        <Grid.Container
          xs={12}
          justify="center"
          align="center"
          className="hc-mt-lg"
        >
          <Grid.Item xs={12} className={styles.checksWrapper}>
            <p className="hc-ta-center hc-fs-md">
              {completedChecks >= 4
                ? 'We received the calibration data for the reader!'
                : "Okay, let's do the next check!"}
            </p>
            <p className={styles.checksProgress}>
              &#x2705;{' '}
              <span>
                <strong>{completedChecks} out of 4</strong>
              </span>{' '}
              checks completed
            </p>
          </Grid.Item>

          <Grid.Item xs={12} justify="center" align="center">
            <Button
              label="Next"
              onClick={() => {
                if (completedChecks < 4) {
                  if (completedChecks === 2) {
                    updateState('SWAP_SIDES', directionState);
                  } else {
                    updateState('STARTED', nextDirection);
                  }
                } else if (completedChecks === 4) {
                  dispatch(clearReaderCalibration(activeMac));
                  navigate('/completedCalibration');
                }
              }}
            />
          </Grid.Item>
        </Grid.Container>
      );
    default:
      return (
        <>
          <Grid.Item
            data-testid="mainContent"
            xs={12}
            className="center-flex-block"
          >
            <p className="hc-ta-center hc-fs-md">
              To calibrate the ATR7000, the reader must sense the tag in 4
              separate areas.
            </p>
            <p className="hc-ta-center hc-fs-md">
              The next steps will guide you on where to stand and hold the tag.
            </p>
          </Grid.Item>
          <Grid.Item xs={12}>
            <RfidGraphic placement={'ATR_INTRO_GIF'} />
          </Grid.Item>
          <Grid.Container
            xs={12}
            justify="center"
            align="center"
            className="hc-mt-lg"
          >
            <Grid.Item justify="center" align="center">
              <Button
                label="Back"
                onClick={() => {
                  if (completedChecks > 0) {
                    setIsQuitDialogVisible(true);
                  } else {
                    navigate('/calibrationSetupReader');
                  }
                }}
                type="secondary"
              />
            </Grid.Item>
            <Grid.Item justify="center" align="center">
              <Button
                label="Next"
                onClick={() => {
                  updateState('CONFIRM_POSITION', directionState);
                }}
              />
            </Grid.Item>
            <Dialog
              headingText="Are you sure?"
              bodyText="A calibration session is in progress. If you go back, the current session data will be lost."
              approveButtonText="Yes"
              isVisible={isQuitDialogVisible}
              onApprove={() => {
                setIsQuitDialogVisible(false);
                navigate('/calibrationSetupReader');
                dispatch(clearReaderCalibration(activeMac));
              }}
              onRefuse={() => {
                setIsQuitDialogVisible(false);
              }}
              refuseButtonText="Cancel"
            />
          </Grid.Container>
        </>
      );
  }
};
